import React, { useState, useEffect, useContext, useRef } from 'react';
import './InspectionPage.css';
import LoadingContext from '../contexts/LoadingContext';
import { fetchWIPDetails, insertOutputData, insertDefectData } from '../utils/api';
import OnlineStatus from './OnlineStatus';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import ApiErrorModal from './ApiErrorModal';

const InspectionPage = ({ hideHeader = false, onLogoChange }) => {
  // Add handleRepaired function
  const handleRepaired = () => {
    if (!selectedSalesOrder || !selectedStyle || !selectedColor || !selectedFit || !selectedSize) {
      alert('Please select Sales Order, Style, Colour, Fit, and Size before inspection');
      return;
    }

    // Prepare data for deferred API call
    const postingDatetime = new Date().toISOString();
    
    // Store API call data to be executed after undo timer
    const apiCallData = {
      type: 'insertOutputData',
      params: {
        salesOrder: selectedSalesOrder,
        style: selectedStyle,
        color: selectedColor,
        fit: selectedFit,
        size: selectedSize,
        postingDatetime,
        repaired: 1
      }
    };
    setPendingApiCall(apiCallData);
    pendingApiCallRef.current = apiCallData;
    setInspected(prev => prev + 1);
    startUndoTimer('repaired');
  };
  const loadingContext = useContext(LoadingContext);
  
  // Get user from localStorage
  const storedUser = JSON.parse(localStorage.getItem('frappe_user') || '{}');
  const user = storedUser;

  // Use full URL for cross-origin requests
  const FRAPPE_URL = process.env.REACT_APP_FRAPPE_URL || 'http://pra.localhost:8000';

  const [selectedSalesOrder, setSelectedSalesOrder] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedFit, setSelectedFit] = useState('');
  const [selectedSize, setSelectedSize] = useState('');

  // Layout state - retrieve from localStorage on initial load
  const [isPanelSwapped, setIsPanelSwapped] = useState(() => {
    const saved = localStorage.getItem('panelSwapped');
    return saved ? JSON.parse(saved) : false;
  });

  // Inspection counters - now from API line_stats
  const [lineStats, setLineStats] = useState({
    Rejected: 0,
    Repairs: 0,
    Inspected: 0,
    Passed: 0,
    Defective: 0,
    DHU: 0
  });
  
  // Legacy counters for local actions (will be replaced by API data)
  const [inspected, setInspected] = useState(0);
  const [passed, setPassed] = useState(0);
  const [defectives, setDefectives] = useState(0);
  const [rejected, setRejected] = useState(0);
  const [dhu, setDhu] = useState(0);

  // Button cooldown state
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [lastAction, setLastAction] = useState(null); // 'pass', 'reject', 'alter', 'pass-altered'
  const [undoProgress, setUndoProgress] = useState(0);
  const [undoTimerRef, setUndoTimerRef] = useState(null);
  
  // Pending API call data
  const [pendingApiCall, setPendingApiCall] = useState(null);
  const pendingApiCallRef = useRef(null);

  // Defect selection state
  const [defectSelectionActive, setDefectSelectionActive] = useState(false);
  const [selectedDefects, setSelectedDefects] = useState([]);
  const [pendingAction, setPendingAction] = useState(null); // 'reject' or 'alter'
  const [defectPopup, setDefectPopup] = useState(null); // { area, defects, x, y }
  const [selectedDefectsInPopup, setSelectedDefectsInPopup] = useState([]);

  // Style properties data from API
  const [stylePropertiesData, setStylePropertiesData] = useState([]);
  const [itemProperties, setItemProperties] = useState([]);
  const [defectHistory, setDefectHistory] = useState([]);
  const [customerLogo, setCustomerLogo] = useState(null);
  const [brandLogo, setBrandLogo] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Defect history popup state
  const [defectHistoryPopup, setDefectHistoryPopup] = useState(null); // { area, defects, x, y }

  // API error modal state
  const [apiError, setApiError] = useState(null);

  // Online status tracking
  const { isOnline, isBackendAvailable } = useOnlineStatus();

  // Save panel swap state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('panelSwapped', JSON.stringify(isPanelSwapped));
  }, [isPanelSwapped]);

  // Handle backend availability changes - refresh data and process queue when backend becomes available
  useEffect(() => {
    const handleBackendAvailable = async () => {
      console.log('✅ Backend available - refreshing data and processing queue');
      
      // Refresh data automatically
      try {
        await handleRefreshData();
        console.log('✅ Data refreshed successfully after backend became available');
      } catch (error) {
        console.error('❌ Failed to refresh data after backend became available:', error);
      }
      
      // Process queued API calls
      import('../utils/offlineApiQueue').then(({ offlineApiQueue }) => {
        const queueSize = offlineApiQueue.getQueueSize();
        if (queueSize > 0) {
          console.log(`📤 Processing ${queueSize} queued API calls`);
          offlineApiQueue.processQueue();
        }
      });
    };

    // Listen for backend availability event
    window.addEventListener('backend-available', handleBackendAvailable);

    return () => {
      window.removeEventListener('backend-available', handleBackendAvailable);
    };
  }, []);

  // Listen for API error events from offline queue
  useEffect(() => {
    const handleApiError = (event) => {
      console.log('🚨 API Error event received:', event.detail);
      setApiError(event.detail);
    };

    window.addEventListener('show-api-error', handleApiError);

    return () => {
      window.removeEventListener('show-api-error', handleApiError);
    };
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (undoTimerRef) {
        clearInterval(undoTimerRef);
      }
    };
  }, [undoTimerRef]);

  // Load style properties from localStorage on mount
  useEffect(() => {
    const storedProperties = localStorage.getItem('frappe_style_properties');
    const storedItemProperties = localStorage.getItem('frappe_item_properties');
    const storedLineStats = localStorage.getItem('frappe_line_stats');
    const storedDefectHistory = localStorage.getItem('frappe_defect_history');
    
    if (storedProperties) {
      try {
        const parsedData = JSON.parse(storedProperties);
        setStylePropertiesData(parsedData);
        console.log('Loaded style properties:', parsedData);
        console.log('Available sizes:', [...new Set(parsedData.map(item => item.size).filter(size => size))]);
      } catch (error) {
        console.error('Error parsing style properties:', error);
      }
    } else {
      console.warn('No style properties found in localStorage');
    }

    if (storedItemProperties) {
      try {
        const parsedItemProperties = JSON.parse(storedItemProperties);
        setItemProperties(parsedItemProperties);
        console.log('📦 Loaded item properties from localStorage:');
        parsedItemProperties.forEach(item => {
          console.log(`  Item: ${item.item}`);
          console.log(`    brand_logo: ${item.brand_logo || 'NOT SET'}`);
          console.log(`    customer_logo: ${item.customer_logo || 'NOT SET'}`);
        });
      } catch (error) {
        console.error('Error parsing item properties:', error);
      }
    }

    if (storedLineStats) {
      try {
        const parsedLineStats = JSON.parse(storedLineStats);
        setLineStats(parsedLineStats);
        console.log('Loaded line stats:', parsedLineStats);
      } catch (error) {
        console.error('Error parsing line stats:', error);
      }
    }
    
    if (storedDefectHistory) {
      try {
        const parsedDefectHistory = JSON.parse(storedDefectHistory);
        setDefectHistory(parsedDefectHistory);
        console.log('Loaded defect history:', parsedDefectHistory);
      } catch (error) {
        console.error('Error parsing defect history:', error);
      }
    }
  }, []);

  // Auto-refresh data on component mount (page reload)
  useEffect(() => {
    const autoRefreshData = async () => {
      console.log('Auto-refreshing WIP data on page load...');
      try {
        const result = await fetchWIPDetails();
        
        if (result.success) {
          setStylePropertiesData(result.styles_wip);
          setItemProperties(result.item_properties);
          if (result.line_stats) {
            setLineStats(result.line_stats);
          }
          if (result.defect_history) {
            setDefectHistory(result.defect_history);
          }
          console.log('✓ Auto-refresh successful');
        } else {
          console.warn('Auto-refresh failed, using cached data');
        }
      } catch (error) {
        console.error('Error during auto-refresh:', error);
        console.warn('Using cached data from localStorage');
      }
    };

    autoRefreshData();
  }, []); // Empty dependency array means this runs once on mount

  // Update logos when selected style changes
  useEffect(() => {
    console.log('🎨 Logo effect triggered:', { 
      selectedStyle, 
      itemPropertiesCount: itemProperties.length,
      allItems: itemProperties.map(ip => ip.item)
    });
    if (selectedStyle && itemProperties.length > 0) {
      const selectedItem = itemProperties.find(ip => ip.item === selectedStyle);
      console.log('🔍 Searching for item:', selectedStyle);
      console.log('📋 Available items:', itemProperties.map(ip => ip.item));
      if (selectedItem) {
        console.log('✅ Found matching item:', {
          item: selectedItem.item,
          brand_logo: selectedItem.brand_logo || 'NOT SET',
          customer_logo: selectedItem.customer_logo || 'NOT SET'
        });
        const newCustomerLogo = selectedItem.customer_logo || null;
        const newBrandLogo = selectedItem.brand_logo || null;
        
        setCustomerLogo(newCustomerLogo);
        setBrandLogo(newBrandLogo);
        
        // Update parent component if callback is provided
        if (onLogoChange) {
          onLogoChange.setCustomerLogo(newCustomerLogo);
          onLogoChange.setBrandLogo(newBrandLogo);
        }
      } else {
        // Reset logos if no matching item found
        console.log('❌ No matching item found for style:', selectedStyle);
        setCustomerLogo(null);
        setBrandLogo(null);
        
        // Update parent component if callback is provided
        if (onLogoChange) {
          onLogoChange.setCustomerLogo(null);
          onLogoChange.setBrandLogo(null);
        }
      }
    } else {
      // Reset logos if no style is selected
      console.log('⚠️ No style selected or no item properties available');
      setCustomerLogo(null);
      setBrandLogo(null);
      
      // Update parent component if callback is provided
      if (onLogoChange) {
        onLogoChange.setCustomerLogo(null);
        onLogoChange.setBrandLogo(null);
      }
    }
  }, [selectedStyle, itemProperties, onLogoChange]);

  // Function to refresh style properties data
  const handleRefreshData = async () => {
    // Check if online before attempting refresh
    if (!isOnline) {
      console.log('📵 Cannot refresh data while offline');
      alert('Cannot refresh data while offline. Please check your internet connection.');
      return;
    }

    setIsRefreshing(true);
    console.log('🔄 Refreshing WIP data...');
    try {
      const result = await fetchWIPDetails();
      
      if (result.success) {
        // Update state with fresh data
        setStylePropertiesData(result.styles_wip);
        setItemProperties(result.item_properties);
        if (result.line_stats) {
          setLineStats(result.line_stats);
        }
        if (result.defect_history) {
          setDefectHistory(result.defect_history);
        }
        console.log('✅ Data refresh completed successfully');
      } else {
        const errorMsg = result.error || 'Failed to refresh data. Please try again.';
        console.error('❌ Data refresh failed:', errorMsg);
        
        // Show error modal instead of alert
        setApiError({
          apiName: 'Refresh Data API',
          statusCode: result.statusCode || 'Unknown',
          timestamp: new Date().toLocaleString(),
          errorMessage: errorMsg,
          parameters: {},
          isQueued: false
        });
      }
    } catch (error) {
      console.error('Error refreshing style properties:', error);
      
      // Show error modal instead of alert
      setApiError({
        apiName: 'Refresh Data API',
        statusCode: 'Network Error',
        timestamp: new Date().toLocaleString(),
        errorMessage: error.message || 'Error refreshing data. Please check your connection.',
        parameters: {},
        isQueued: false
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Get unique values for each property
  const getAllSalesOrders = () => {
    const orders = [...new Set(stylePropertiesData.map(item => item.PO))];
    return orders.sort();
  };

  const getAllStyles = () => {
    const styles = [...new Set(stylePropertiesData.map(item => item.style))];
    return styles.sort();
  };

  const getAllColors = () => {
    const colors = [...new Set(stylePropertiesData.map(item => item.colour))];
    return colors.sort();
  };

  const getAllFits = () => {
    const fits = [...new Set(stylePropertiesData.map(item => item.fit || 'N/A'))];
    return fits.sort();
  };

  const getAllSizes = () => {
    const sizes = [...new Set(stylePropertiesData.map(item => item.size).filter(size => size))];
    return sizes.sort();
  };

  // Get allowed values based on selections
  const getAllowedStyles = () => {
    if (!selectedSalesOrder) return [];
    return [...new Set(
      stylePropertiesData
        .filter(item => item.PO === selectedSalesOrder)
        .map(item => item.style)
    )].sort();
  };

  const getAllowedColors = () => {
    if (!selectedSalesOrder || !selectedStyle) return [];
    return [...new Set(
      stylePropertiesData
        .filter(item => item.PO === selectedSalesOrder && item.style === selectedStyle)
        .map(item => item.colour)
    )].sort();
  };

  const getAllowedFits = () => {
    if (!selectedSalesOrder || !selectedStyle || !selectedColor) return [];
    return [...new Set(
      stylePropertiesData
        .filter(item => 
          item.PO === selectedSalesOrder && 
          item.style === selectedStyle && 
          item.colour === selectedColor
        )
        .map(item => item.fit || 'N/A')
    )].sort();
  };

  const getAllowedSizes = () => {
    if (!selectedSalesOrder || !selectedStyle || !selectedColor || !selectedFit) return [];
    return [...new Set(
      stylePropertiesData
        .filter(item => 
          item.PO === selectedSalesOrder && 
          item.style === selectedStyle && 
          item.colour === selectedColor && 
          (item.fit || 'N/A') === selectedFit
        )
        .map(item => item.size)
    )].sort();
  };

  // Reset dependent selections when parent changes
  useEffect(() => {
    setSelectedStyle('');
    setSelectedColor('');
    setSelectedFit('');
    setSelectedSize('');
  }, [selectedSalesOrder]);

  useEffect(() => {
    setSelectedColor('');
    setSelectedFit('');
    setSelectedSize('');
  }, [selectedStyle]);

  useEffect(() => {
    setSelectedFit('');
    setSelectedSize('');
  }, [selectedColor]);

  useEffect(() => {
    setSelectedSize('');
  }, [selectedFit]);

  // Auto-select when only one option is available
  useEffect(() => {
    if (selectedSalesOrder && !selectedStyle) {
      const allowedStyles = getAllowedStyles();
      if (allowedStyles.length === 1) {
        console.log('Auto-selecting style:', allowedStyles[0]);
        setSelectedStyle(allowedStyles[0]);
      }
    }
  }, [selectedSalesOrder, selectedStyle]);

  useEffect(() => {
    if (selectedSalesOrder && selectedStyle && !selectedColor) {
      const allowedColors = getAllowedColors();
      if (allowedColors.length === 1) {
        console.log('Auto-selecting color:', allowedColors[0]);
        setSelectedColor(allowedColors[0]);
      }
    }
  }, [selectedSalesOrder, selectedStyle, selectedColor]);

  useEffect(() => {
    if (selectedSalesOrder && selectedStyle && selectedColor && !selectedFit) {
      const allowedFits = getAllowedFits();
      if (allowedFits.length === 1) {
        console.log('Auto-selecting fit:', allowedFits[0]);
        setSelectedFit(allowedFits[0]);
      }
    }
  }, [selectedSalesOrder, selectedStyle, selectedColor, selectedFit]);

  useEffect(() => {
    if (selectedSalesOrder && selectedStyle && selectedColor && selectedFit && !selectedSize) {
      const allowedSizes = getAllowedSizes();
      if (allowedSizes.length === 1) {
        console.log('Auto-selecting size:', allowedSizes[0]);
        setSelectedSize(allowedSizes[0]);
      }
    }
  }, [selectedSalesOrder, selectedStyle, selectedColor, selectedFit, selectedSize]);

  // Check if an option is allowed
  const isStyleAllowed = (style) => {
    if (!selectedSalesOrder) return false;
    return getAllowedStyles().includes(style);
  };

  const isColorAllowed = (color) => {
    if (!selectedSalesOrder || !selectedStyle) return false;
    return getAllowedColors().includes(color);
  };

  const isFitAllowed = (fit) => {
    if (!selectedSalesOrder || !selectedStyle || !selectedColor) return false;
    return getAllowedFits().includes(fit);
  };

  const isSizeAllowed = (size) => {
    if (!selectedSalesOrder || !selectedStyle || !selectedColor || !selectedFit) return false;
    return getAllowedSizes().includes(size);
  };

  const salesOrders = getAllSalesOrders();
  const styles = getAllStyles();
  const colors = getAllColors();
  const fits = getAllFits();
  const sizes = getAllSizes();

  const handleLogout = async () => {
    const { setLoading } = loadingContext;
    try {
      // show global loader while logging out
      setLoading(true);
      
      // Call Frappe logout API
      await fetch(`${FRAPPE_URL}/api/method/logout`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });
      
      // Clear localStorage
      localStorage.removeItem('frappe_user');
      localStorage.removeItem('frappe_logged_in');
      localStorage.removeItem('frappe_user_line');
      
      // Reload to show login page
      window.location.reload();
    } catch (error) {
      console.error('Logout error:', error);
      
      // Even if API call fails, clear local state and reload
      localStorage.removeItem('frappe_user');
      localStorage.removeItem('frappe_logged_in');
      localStorage.removeItem('frappe_user_line');
      
      window.location.reload();
    } finally {
      // In environments where reload might not occur immediately, hide loader as a fallback
      setTimeout(() => {
        setLoading(false);
      }, 3000);
    }
  };

    const handlePass = () => {
    if (!selectedSalesOrder || !selectedStyle || !selectedColor || !selectedFit || !selectedSize) {
      alert('Please select Sales Order, Style, Colour, Fit, and Size before inspection');
      return;
    }

    // Prepare data for deferred API call
    const postingDatetime = new Date().toISOString();
    
    console.log('📋 Preparing deferred output data');
    
    // Store API call data to be executed after undo timer
    const apiCallData = {
      type: 'insertOutputData',
      params: {
        salesOrder: selectedSalesOrder,
        style: selectedStyle,
        color: selectedColor,
        fit: selectedFit,
        size: selectedSize,
        postingDatetime,
        repaired: 0
      }
    };
    
    setPendingApiCall(apiCallData);
    pendingApiCallRef.current = apiCallData;
    
    console.log('✅ Pending API call stored:', apiCallData);
    
    // Update local counters immediately for UI responsiveness (these will be refreshed from API)
    setInspected(prev => prev + 1);
    setPassed(prev => prev + 1);
    
    // Start undo timer
    startUndoTimer('pass');
  };

  const handleReject = () => {
    if (!selectedSalesOrder || !selectedStyle || !selectedColor || !selectedFit || !selectedSize) {
      alert('Please select Sales Order, Style, Colour, Fit, and Size before inspection');
      return;
    }
    
    // Enable defect selection mode
    setDefectSelectionActive(true);
    setPendingAction('reject');
    setSelectedDefects([]);
  };

  const handleAlter = () => {
    if (!selectedSalesOrder || !selectedStyle || !selectedColor || !selectedFit || !selectedSize) {
      alert('Please select Sales Order, Style, Colour, Fit, and Size before inspection');
      return;
    }
    
    // Enable defect selection mode
    setDefectSelectionActive(true);
    setPendingAction('alter');
    setSelectedDefects([]);
  };

  const handleDefectClick = (defectArea, event) => {
    if (!defectSelectionActive) return;
    
    // Open popup with defects for this area
    setDefectPopup({
      area: defectArea.area,
      defects: defectArea.defects,
      x: event.clientX,
      y: event.clientY
    });
    
    // Check if this area already has selected defects
    const existingSelection = selectedDefects.find(d => d.area === defectArea.area);
    setSelectedDefectsInPopup(existingSelection ? existingSelection.selectedDefects : []);
  };

  const handleDefectCheckboxChange = (defect) => {
    setSelectedDefectsInPopup(prev => {
      const existingDefect = prev.find(d => d.id === defect.id);
      if (existingDefect) {
        return prev.filter(d => d.id !== defect.id);
      } else {
        return [...prev, defect];
      }
    });
  };

  const confirmDefectPopup = () => {
    // Allow confirming even with no defects selected (to clear previous selections)
    // Add or update the selected defects for this area
    setSelectedDefects(prev => {
      const filtered = prev.filter(d => d.area !== defectPopup.area);
      
      // Only add to selectedDefects if there are defects selected
      if (selectedDefectsInPopup.length > 0) {
        return [...filtered, {
          area: defectPopup.area,
          selectedDefects: selectedDefectsInPopup,
          allDefects: defectPopup.defects
        }];
      }
      
      // If no defects selected, just return filtered (removes this area from selection)
      return filtered;
    });

    // Close popup
    setDefectPopup(null);
    setSelectedDefectsInPopup([]);
  };

  const closeDefectPopup = () => {
    setDefectPopup(null);
    setSelectedDefectsInPopup([]);
  };

  const confirmDefectSelection = () => {
    if (selectedDefects.length === 0) {
      alert('Please select at least one defect area');
      return;
    }

    // Prepare defect data for API call
    const defectIds = selectedDefects.flatMap(defectArea => 
      defectArea.selectedDefects.map(defect => defect.id)
    );
    
    console.log('Selected defect IDs:', defectIds);

    // Process the pending action
    if (pendingAction === 'reject') {
      setInspected(prev => prev + 1);
      setRejected(prev => prev + 1);
      setDefectives(prev => prev + 1);
      
      // Prepare defect API call data
      const defectApiCallData = {
        type: 'insertDefectData',
        params: {
          salesOrder: selectedSalesOrder,
          item: selectedStyle,
          colour: selectedColor,
          fit: selectedFit,
          size: selectedSize,
          outputQty: 1,
          repairable: false, // Rejected items are not repairable
          defectIds: defectIds
        }
      };
      
      // Store pending API call
      pendingApiCallRef.current = defectApiCallData;
      console.log('💾 Stored pending defect API call (reject):', defectApiCallData);
      
      // Start undo timer
      startUndoTimer('reject');
    } else if (pendingAction === 'alter') {
      setInspected(prev => prev + 1);
      setDefectives(prev => prev + 1);
      
      // Prepare defect API call data
      const defectApiCallData = {
        type: 'insertDefectData',
        params: {
          salesOrder: selectedSalesOrder,
          item: selectedStyle,
          colour: selectedColor,
          fit: selectedFit,
          size: selectedSize,
          outputQty: 1,
          repairable: true, // Altered items are repairable
          defectIds: defectIds
        }
      };
      
      // Store pending API call
      pendingApiCallRef.current = defectApiCallData;
      console.log('💾 Stored pending defect API call (alter):', defectApiCallData);
      
      // Start undo timer
      startUndoTimer('alter');
    }

    // Reset defect selection state
    setDefectSelectionActive(false);
    setPendingAction(null);
    setSelectedDefects([]);
  };

  const cancelDefectSelection = () => {
    setDefectSelectionActive(false);
    setPendingAction(null);
    setSelectedDefects([]);
  };

  const startUndoTimer = (action) => {
    setLastAction(action);
    setButtonsDisabled(true);
    setUndoProgress(100);

    // Clear any existing timer
    if (undoTimerRef) {
      clearInterval(undoTimerRef);
    }

    const duration = 10000; // 10 seconds
    const interval = 50; // Update every 50ms
    const steps = duration / interval;
    let currentStep = 0;

    const timer = setInterval(async () => {
      currentStep++;
      const progress = 100 - (currentStep / steps) * 100;
      setUndoProgress(progress);

      if (currentStep >= steps) {
        clearInterval(timer);
        setButtonsDisabled(false);
        setLastAction(null);
        setUndoProgress(0);
        
        // Execute pending API call when timer expires
        if (pendingApiCallRef.current) {
          console.log('⏰ Undo timer expired, executing API call...', pendingApiCallRef.current);
          await executePendingApiCall();
        } else {
          console.log('⏰ Undo timer expired, but no pending API call found');
        }
      }
    }, interval);

    setUndoTimerRef(timer);
  };

  // Function to execute pending API calls
  const executePendingApiCall = async () => {
    const apiCallData = pendingApiCallRef.current;
    if (!apiCallData) return;
    
    try {
      if (apiCallData.type === 'insertOutputData') {
        const { salesOrder, style, color, fit, size, postingDatetime, repaired } = apiCallData.params;
        
        console.log('🚀 Executing deferred API call for output data...');
        
        const result = await insertOutputData(
          salesOrder,
          style,
          color,
          fit,
          size,
          postingDatetime,
          repaired
        );

        if (result.success) {
          if (result.queued) {
            console.log('📤 Output data queued for when online');
          } else {
            console.log('✅ Deferred output data inserted successfully');
          }
          
          // Trigger data refresh after successful API call (only if not queued)
          if (!result.queued) {
            console.log('🔄 Triggering data refresh after successful output entry...');
            try {
              await handleRefreshData();
              console.log('✅ Data refreshed successfully after output entry');
            } catch (refreshError) {
              console.warn('⚠️ Data refresh failed after output entry (non-critical):', refreshError);
              // Don't show error to user as this is non-critical
            }
          }
        } else {
          console.error('❌ Failed to insert deferred output data:', result.error);
          
          // Provide more specific error messages
          let errorMessage = 'Failed to save data to server';
          
          if (result.isCSRFError) {
            errorMessage = 'Authentication issue. Please refresh the page and try again.';
          } else if (result.error) {
            errorMessage = result.error;
          } else {
            errorMessage = 'Unknown error occurred';
          }
          
          // Show error modal instead of alert
          setApiError({
            apiName: 'Output/Pass API',
            statusCode: result.statusCode || 'Unknown',
            timestamp: new Date().toLocaleString(),
            errorMessage: errorMessage,
            parameters: {
              salesOrder,
              style,
              color,
              fit,
              size,
              postingDatetime,
              repaired
            },
            isQueued: false
          });
          
          // Revert the UI counters since API failed
          setInspected(prev => prev - 1);
          setPassed(prev => prev - 1);
        }
      } else if (apiCallData.type === 'insertDefectData') {
        const { salesOrder, item, colour, fit, size, outputQty, repairable, defectIds } = apiCallData.params;
        
        console.log('🚀 Executing deferred API call for defect data...');
        console.log('🔍 Defect API params:', apiCallData.params);
        
        const result = await insertDefectData(
          salesOrder,
          item,
          colour,
          fit,
          size,
          outputQty,
          repairable,
          defectIds
        );

        if (result.success) {
          if (result.queued) {
            console.log('📤 Defect data queued for when online');
          } else {
            console.log('✅ Deferred defect data inserted successfully');
          }
          
          // Trigger data refresh after successful API call (only if not queued)
          if (!result.queued) {
            console.log('🔄 Triggering data refresh after successful defect entry...');
            try {
              await handleRefreshData();
              console.log('✅ Data refreshed successfully after defect entry');
            } catch (refreshError) {
              console.warn('⚠️ Data refresh failed after defect entry (non-critical):', refreshError);
              // Don't show error to user as this is non-critical
            }
          }
        } else {
          console.error('❌ Failed to insert deferred defect data:', result.error);
          
          // Provide more specific error messages
          let errorMessage = 'Failed to save defect data to server';
          
          if (result.isCSRFError) {
            errorMessage = 'Authentication issue. Please refresh the page and try again.';
          } else if (result.error) {
            errorMessage = result.error;
          } else {
            errorMessage = 'Unknown error occurred while saving defect data';
          }
          
          // Show error modal instead of alert
          setApiError({
            apiName: 'Defect API',
            statusCode: result.statusCode || 'Unknown',
            timestamp: new Date().toLocaleString(),
            errorMessage: errorMessage,
            parameters: {
              salesOrder,
              item,
              colour,
              fit,
              size,
              outputQty,
              repairable,
              defectIds
            },
            isQueued: false
          });
          
          // Revert the UI counters since API failed
          setInspected(prev => prev - 1);
          setDefectives(prev => prev - 1);
          
          // Revert specific counters based on action
          if (!repairable) { // This was a reject action
            setRejected(prev => prev - 1);
          }
        }
      }
    } catch (error) {
      console.error('❌ Error executing deferred API call:', error);
      
      if (apiCallData.type === 'insertDefectData') {
        // Show error modal for defect data
        setApiError({
          apiName: 'Defect API',
          statusCode: 'Network Error',
          timestamp: new Date().toLocaleString(),
          errorMessage: error.message || 'Error saving defect data to server. Please try again.',
          parameters: apiCallData.params,
          isQueued: false
        });
        
        // Revert the UI counters since API failed
        setInspected(prev => prev - 1);
        setDefectives(prev => prev - 1);
        
        // Revert specific counters based on action
        if (!apiCallData.params.repairable) { // This was a reject action
          setRejected(prev => prev - 1);
        }
      } else {
        // Show error modal for output data
        setApiError({
          apiName: 'Output/Pass API',
          statusCode: 'Network Error',
          timestamp: new Date().toLocaleString(),
          errorMessage: error.message || 'Error saving data to server. Please try again.',
          parameters: apiCallData.params,
          isQueued: false
        });
        
        // Revert the UI counters since API failed
        setInspected(prev => prev - 1);
        setPassed(prev => prev - 1);
      }
    } finally {
      // Clear the pending API call
      setPendingApiCall(null);
      pendingApiCallRef.current = null;
    }
  };

  const handleUndo = () => {
    if (!lastAction) return;

    console.log('🔄 Undo action triggered, canceling pending API call');

    // Cancel pending API call
    setPendingApiCall(null);
    pendingApiCallRef.current = null;

    // Reverse the action
    switch (lastAction) {
      case 'pass':
        setInspected(prev => prev - 1);
        setPassed(prev => prev - 1);
        break;
      case 'reject':
        setInspected(prev => prev - 1);
        setRejected(prev => prev - 1);
        setDefectives(prev => prev - 1);
        break;
      case 'alter':
        setInspected(prev => prev - 1);
        setDefectives(prev => prev - 1);
        break;
      default:
        break;
    }

    // Clear timer and reset state
    if (undoTimerRef) {
      clearInterval(undoTimerRef);
    }
    setButtonsDisabled(false);
    setLastAction(null);
    setUndoProgress(0);
  };

  return (
    <div className="inspection-page">
      {!hideHeader && (
        <>
          <header className="inspection-header">
            <div className="logo-section">
              <img 
                src="/pragyaa-logo.png"
                alt="Pragyaa Apparels" 
                className="app-logo"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'block';
                }}
              />
              <h1 className="app-title" style={{ display: 'none' }}>Pragyaa Apparels</h1>
            </div>
            
            {/* Customer and Brand Logos */}
            <div className="client-logos">
              {customerLogo && (
                <img 
                  src={`${FRAPPE_URL}${customerLogo}`}
                  alt="Customer Logo" 
                  className="client-logo customer-logo"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              {brandLogo && (
                <img 
                  src={`${FRAPPE_URL}${brandLogo}`}
                  alt="Brand Logo" 
                  className="client-logo brand-logo"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
            </div>
            
            <div className="user-info">
              <span className="welcome-text">
                {user?.full_name || user?.name || user?.username || 'User'}
              </span>
              <button 
                onClick={handleLogout} 
                className="logout-button"
                disabled={!isOnline || !isBackendAvailable}
                title={!isOnline || !isBackendAvailable ? "Cannot sign out - backend unavailable" : "Sign Out"}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sign Out
              </button>
            </div>
          </header>
        </>
      )}

      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-label">Inspected:</span>
          <span className="stat-value blue">{String(lineStats.Inspected || 0).padStart(4, '0')}</span>
        </div>
        <div className="stat-divider">|</div>
        <div className="stat-item">
          <span className="stat-label">Output:</span>
          <span className="stat-value blue">{String((lineStats.Passed || 0) + (lineStats.Repaired || lineStats.Repairs || 0)).padStart(4, '0')}</span>
        </div>
        <div className="stat-divider">|</div>
        <div className="stat-item">
          <span className="stat-label">Passed:</span>
          <span className="stat-value green">{String(lineStats.Passed || 0).padStart(4, '0')}</span>
        </div>
        <div className="stat-divider">|</div>
        <div className="stat-item">
          <span className="stat-label">Repaired:</span>
          <span className="stat-value orange">{String(lineStats.Repaired || lineStats.Repairs || 0).padStart(3, '0')}</span>
        </div>
        <div className="stat-divider">|</div>
        <div className="stat-item">
          <span className="stat-label">Defected:</span>
          <span className="stat-value orange">{String(lineStats.Defected || 0).padStart(3, '0')}</span>
        </div>
        <div className="stat-divider">|</div>
        <div className="stat-item">
          <span className="stat-label">Damaged:</span>
          <span className="stat-value red">{String(lineStats.Damaged || 0).padStart(3, '0')}</span>
        </div>
        <div className="stat-divider">|</div>
        <div className="stat-item">
          <span className="stat-label">DHU:</span>
          <span className="stat-value black">{(lineStats.DHU || 0).toFixed(2)}</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <OnlineStatus />
          <button 
            onClick={() => setIsPanelSwapped(!isPanelSwapped)}
            className="swap-panels-button" 
            title="Swap Panels"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
          </button>
          <button 
            onClick={handleRefreshData} 
            className="refresh-button" 
            title={!isOnline || !isBackendAvailable ? "Cannot refresh - backend unavailable" : "Refresh"}
            disabled={isRefreshing || !isOnline || !isBackendAvailable}
          >
            <svg 
              width="18" 
              height="18" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor"
              className={isRefreshing ? 'spinning' : ''}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {isRefreshing ? 'Refreshing...' : (!isOnline ? 'Offline' : 'Refresh')}
          </button>
        </div>
      </div>

      <div className={`inspection-content ${isPanelSwapped ? 'swapped' : ''}`}>
        <div className="inspection-area">
          <div className="instruction-card">
            {/* Image Area - 80% */}
            <div style={{ width: '100%', height: '80%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
              {selectedStyle && itemProperties.find(ip => ip.item === selectedStyle) ? (
                <div 
                  className="style-image-container"
                >
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img 
                      src={`${FRAPPE_URL}${itemProperties.find(ip => ip.item === selectedStyle).image}`} 
                      alt={selectedStyle}
                      className="style-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.nextSibling.style.display = 'flex';
                      }}
                    />
                    {/* Blue Defect markers - only visible when defect selection is active */}
                    {defectSelectionActive && itemProperties.find(ip => ip.item === selectedStyle)?.defect_areas?.map((area, index) => {
                      const selectedDefect = selectedDefects.find(d => d.area === area.area);
                      const hasSelectedDefects = selectedDefect && selectedDefect.selectedDefects.length > 0;
                      return (
                        <div
                          key={index}
                          className="defect-marker"
                          onClick={(e) => handleDefectClick(area, e)}
                          style={{
                            position: 'absolute',
                            left: `${area.x_coordinates}%`,
                            top: `${area.y_coordinates}%`,
                            transform: 'translate(-50%, -50%)',
                            width: '20px',
                            height: '20px',
                            backgroundColor: hasSelectedDefects ? '#f44336' : '#2196F3',
                            borderRadius: '50%',
                            border: hasSelectedDefects ? '3px solid #fff' : '3px solid white',
                            boxShadow: hasSelectedDefects ? '0 2px 12px rgba(244, 67, 54, 0.6)' : '0 2px 8px rgba(0,0,0,0.3)',
                            cursor: 'pointer',
                            zIndex: 10,
                            transition: 'all 0.2s ease',
                            animation: 'fadeIn 0.3s ease-in'
                          }}
                          title={`${area.area}: ${area.defects.join(', ')}`}
                        />
                      );
                    })}
                    
                    {/* Defect history markers - visible when NOT in defect selection mode */}
                    {!defectSelectionActive && selectedStyle && defectHistory
                      .filter(history => history.item === selectedStyle)
                      .map((history, index) => {
                        const markerColor = history.point_colour || '#f44336';
                        return (
                          <div
                            key={`history-${index}`}
                            className="defect-history-marker"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDefectHistoryPopup({
                                area: history.area,
                                defects: history.defects,
                                total: history.total_defects,
                                x: e.clientX,
                                y: e.clientY
                              });
                            }}
                            style={{
                              position: 'absolute',
                              left: `${history.x}%`,
                              top: `${history.y}%`,
                              transform: 'translate(-50%, -50%)',
                              width: '25px',
                              height: '25px',
                              backgroundColor: markerColor,
                              borderRadius: '50%',
                              border: '3px solid #fff',
                              boxShadow: `0 2px 12px ${markerColor}99`,
                              cursor: 'pointer',
                              zIndex: 10,
                              transition: 'all 0.2s ease',
                              animation: 'fadeIn 0.3s ease-in',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'black',
                              fontWeight: 'bold',
                              fontSize: '10px'
                            }}
                            title={`${history.area}: ${history.defects}`}
                          >
                            {Math.round(history.total_defects)}
                          </div>
                        );
                      })}
                  </div>
                  <div className="hand-icon" style={{ display: 'none' }}>
                    <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#999">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                    </svg>
                  </div>
                </div>
              ) : (
                <div className="hand-icon">
                  <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#999">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                  </svg>
                  <h2 style={{ color: '#ccc', fontSize: '1rem', marginTop: '1rem' }}>Select all fields to start inspection</h2>
                </div>
              )}
            </div>

            {/* Confirm/Cancel buttons area - 20% - Always present to prevent layout shift */}
            <div className="defect-confirmation-area" style={{ 
              visibility: defectSelectionActive ? 'visible' : 'hidden',
              opacity: defectSelectionActive ? 1 : 0,
              transition: 'opacity 0.2s ease-in-out'
            }}>
              <button
                onClick={confirmDefectSelection}
                disabled={!defectSelectionActive}
                style={{
                  flex: 1,
                  height: '100%',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: defectSelectionActive ? 'pointer' : 'default',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  maxWidth: '200px'
                }}
              >
                Confirm ({selectedDefects.length})
              </button>
              <button
                onClick={cancelDefectSelection}
                disabled={!defectSelectionActive}
                style={{
                  flex: 1,
                  height: '100%',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: defectSelectionActive ? 'pointer' : 'default',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  maxWidth: '200px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>

          <div className="action-buttons">
            <div className="action-buttons-row">
              <button 
                className="action-btn reject-btn" 
                onClick={lastAction === 'reject' ? handleUndo : handleReject}
                disabled={defectSelectionActive || (buttonsDisabled && lastAction !== 'reject') || (!selectedSalesOrder || !selectedStyle || !selectedColor || !selectedFit || !selectedSize)}
                style={{ position: 'relative', overflow: 'hidden' }}
              >
                {lastAction === 'reject' && (
                  <div 
                    className="undo-progress-bar" 
                    style={{ width: `${undoProgress}%` }}
                  />
                )}
                <span>{lastAction === 'reject' ? 'UNDO' : 'DAMAGED'}</span>
              </button>
              <button 
                className="action-btn alter-btn" 
                onClick={lastAction === 'alter' ? handleUndo : handleAlter}
                disabled={defectSelectionActive || (buttonsDisabled && lastAction !== 'alter') || (!selectedSalesOrder || !selectedStyle || !selectedColor || !selectedFit || !selectedSize)}
                style={{ position: 'relative', overflow: 'hidden' }}
              >
                {lastAction === 'alter' && (
                  <div 
                    className="undo-progress-bar" 
                    style={{ width: `${undoProgress}%` }}
                  />
                )}
                <span>{lastAction === 'alter' ? 'UNDO' : 'DEFECTED'}</span>
              </button>
            </div>
            <div className="action-buttons-row-large">
              <button 
                className="action-btn-large repaired-btn-large" 
                onClick={lastAction === 'repaired' ? handleUndo : handleRepaired}
                disabled={defectSelectionActive || (buttonsDisabled && lastAction !== 'repaired') || (!selectedSalesOrder || !selectedStyle || !selectedColor || !selectedFit || !selectedSize)}
                style={{ position: 'relative', overflow: 'hidden', marginRight: '0.5rem' }}
              >
                {lastAction === 'repaired' && (
                  <div 
                    className="undo-progress-bar" 
                    style={{ width: `${undoProgress}%` }}
                  />
                )}
                <span>{
                  lastAction === 'repaired' 
                    ? 'UNDO' 
                    : 'REPAIRED'
                }</span>
              </button>
              <button 
                className="action-btn-large pass-btn-large" 
                onClick={lastAction === 'pass' ? handleUndo : handlePass}
                disabled={defectSelectionActive || (buttonsDisabled && lastAction !== 'pass') || (!selectedSalesOrder || !selectedStyle || !selectedColor || !selectedFit || !selectedSize)}
                style={{ position: 'relative', overflow: 'hidden' }}
              >
                {lastAction === 'pass' && (
                  <div 
                    className="undo-progress-bar" 
                    style={{ width: `${undoProgress}%` }}
                  />
                )}
                <span>{
                  lastAction === 'pass' 
                    ? 'UNDO' 
                    : 'PASS'
                }</span>
              </button>
            </div>
          </div>
        </div>

        <div className="selection-panel">
          <div className="selection-section">
            <h3>Sales Order</h3>
            <div className="selection-grid">
              {salesOrders.map(order => (
                <button
                  key={order}
                  className={`selection-btn ${selectedSalesOrder === order ? 'selected' : ''}`}
                  onClick={() => setSelectedSalesOrder(order)}
                >
                  {order}
                </button>
              ))}
            </div>
          </div>

          <div className="selection-section">
            <h3>Style</h3>
            <div className="selection-grid">
              {styles.map(style => {
                const isAllowed = isStyleAllowed(style);
                return (
                  <button
                    key={style}
                    className={`selection-btn ${selectedStyle === style ? 'selected' : ''} ${!isAllowed ? 'disabled' : ''}`}
                    onClick={() => isAllowed && setSelectedStyle(style)}
                    disabled={!isAllowed}
                  >
                    {style}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="selection-section">
            <h3>Colour</h3>
            <div className="selection-grid">
              {colors.map(color => {
                const isAllowed = isColorAllowed(color);
                return (
                  <button
                    key={color}
                    className={`selection-btn ${selectedColor === color ? 'selected' : ''} ${!isAllowed ? 'disabled' : ''}`}
                    onClick={() => isAllowed && setSelectedColor(color)}
                    disabled={!isAllowed}
                  >
                    {color}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="selection-section">
            <h3>Fit</h3>
            <div className="selection-grid">
              {fits.map(fit => {
                const isAllowed = isFitAllowed(fit);
                return (
                  <button
                    key={fit}
                    className={`selection-btn ${selectedFit === fit ? 'selected' : ''} ${!isAllowed ? 'disabled' : ''}`}
                    onClick={() => isAllowed && setSelectedFit(fit)}
                    disabled={!isAllowed}
                  >
                    {fit}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="selection-section">
            <h3>Size</h3>
            <div className="size-selection-grid">
              {sizes.map(size => {
                const isAllowed = isSizeAllowed(size);
                return (
                  <button
                    key={size}
                    className={`selection-btn ${selectedSize === size ? 'selected' : ''} ${!isAllowed ? 'disabled' : ''}`}
                    onClick={() => isAllowed && setSelectedSize(size)}
                    disabled={!isAllowed}
                  >
                    {size}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Defect Selection Popup Modal */}
      {defectPopup && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={closeDefectPopup}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '300px',
              maxWidth: '400px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              animation: 'slideIn 0.2s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '18px',
              fontWeight: '600',
              color: '#333'
            }}>
              Select Defects - {defectPopup.area}
            </h3>
            
            <div style={{
              marginBottom: '20px',
              maxHeight: '300px',
              overflowY: 'auto'
            }}>
              {defectPopup.defects.map((defect, index) => (
                <label
                  key={defect.id || index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px',
                    marginBottom: '8px',
                    backgroundColor: selectedDefectsInPopup.find(d => d.id === defect.id) ? '#e3f2fd' : '#f5f5f5',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    border: selectedDefectsInPopup.find(d => d.id === defect.id) ? '2px solid #2196F3' : '2px solid transparent'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!selectedDefectsInPopup.find(d => d.id === defect.id)}
                    onChange={() => handleDefectCheckboxChange(defect)}
                    style={{
                      width: '18px',
                      height: '18px',
                      marginRight: '12px',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{
                    fontSize: '15px',
                    color: '#333',
                    fontWeight: selectedDefectsInPopup.find(d => d.id === defect.id) ? '600' : '400'
                  }}>
                    {defect.name || defect}
                  </span>
                </label>
              ))}
            </div>

            <div style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={closeDefectPopup}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#e0e0e0',
                  color: '#333',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDefectPopup}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Confirm ({selectedDefectsInPopup.length})
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Defect History Popup Modal */}
      {defectHistoryPopup && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setDefectHistoryPopup(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '300px',
              maxWidth: '500px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              animation: 'slideIn 0.2s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '18px',
              fontWeight: '600',
              color: '#333'
            }}>
              Defect History - {defectHistoryPopup.area}
            </h3>
            
            <div style={{
              marginBottom: '20px',
              padding: '16px',
              backgroundColor: '#fff3e0',
              borderRadius: '8px',
              border: '2px solid #ff9800'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#e65100',
                fontWeight: '600',
                marginBottom: '8px'
              }}>
                Total Defects: {Math.round(defectHistoryPopup.total)}
              </div>
              <div style={{
                fontSize: '15px',
                color: '#333',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap'
              }}>
                {defectHistoryPopup.defects}
              </div>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setDefectHistoryPopup(null)}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Error Modal */}
      {apiError && (
        <ApiErrorModal 
          error={apiError} 
          onClose={() => setApiError(null)} 
        />
      )}
    </div>
  );
};

export default InspectionPage;
